import React, { useState, useEffect, useCallback, useRef } from "react";
import ChatSidebar from "./ChatSidebar";
import MessageBox from "./MessageBox";
import Sidebar from "../Sidebar/Sidebar";
import useUnreadSocket from "../hooks/useUnreadSocket";
import { getUnreadCount } from "../Apis/ChatApis";

const Chat = () => {
  const [selectedChat, setSelectedChat] = useState(null);
  const [unreadData, setUnreadData] = useState({
    groupUnread: 0,
    personalUnread: 0,
    totalUnread: 0,
    unreadBySender: []
  });
  const [userRole, setUserRole] = useState(null);
  const selectedChatRef = useRef(selectedChat);
  const messageBoxRef = useRef(null);
  const previousChatIdRef = useRef(null);

  useEffect(() => {
    selectedChatRef.current = selectedChat;
  }, [selectedChat]);

  const filterUnreadForOpenChat = useCallback((data, currentSelectedChat) => {
    let filteredUnread = data.unreadBySender || [];

    if (currentSelectedChat) {
      filteredUnread = filteredUnread.filter(item => {
        if (currentSelectedChat.type === "admin" && item.admin_id === currentSelectedChat.receiver_admin_id) {
          return false;
        }
        if (currentSelectedChat.type === "superadmin" && item.superadmin_id === currentSelectedChat.receiver_superadmin_id) {
          return false;
        }
        return true;
      });
    }

    const totalPersonalUnread = filteredUnread.reduce((sum, item) => sum + (item.count || 0), 0);
    const groupUnread = (currentSelectedChat?.type === "group") ? 0 : (data.groupUnread || 0);

    return {
      groupUnread: groupUnread,
      personalUnread: totalPersonalUnread,
      totalUnread: groupUnread + totalPersonalUnread,
      unreadBySender: filteredUnread
    };
  }, []);

  const handleUpdate = useCallback(async (data) => {
    const filtered = filterUnreadForOpenChat(data, selectedChatRef.current);
    setUnreadData({
      groupUnread: filtered.groupUnread,
      personalUnread: filtered.personalUnread,
      totalUnread: filtered.totalUnread,
      unreadBySender: filtered.unreadBySender
    });
  }, [filterUnreadForOpenChat]);

  const handleNewMessage = useCallback((newMessage) => {
    console.log("New message received:", newMessage);
    
    const isForCurrentChat = () => {
      if (!selectedChatRef.current) return false;
      
      if (selectedChatRef.current.type === "group") {
        return newMessage.is_group === true;
      }
      
      if (selectedChatRef.current.type === "admin") {
        return newMessage.receiver_admin_id === selectedChatRef.current.receiver_admin_id ||
               newMessage.sender_id === selectedChatRef.current.receiver_admin_id;
      }
      
      if (selectedChatRef.current.type === "superadmin") {
        return newMessage.receiver_superadmin_id === selectedChatRef.current.receiver_superadmin_id ||
               newMessage.sender_id === selectedChatRef.current.receiver_superadmin_id;
      }
      
      return false;
    };
    
    if (isForCurrentChat() && messageBoxRef.current) {
      messageBoxRef.current.addNewMessage(newMessage);
    }
  }, []);

  const handleMarkRead = useCallback((messageIds) => {
    console.log("Messages marked as read:", messageIds);
  }, []);

  const handleEditMessage = useCallback((updatedMessage) => {
    if (messageBoxRef.current) {
      messageBoxRef.current.updateEditedMessage(updatedMessage);
    }
  }, []);

  const handleDeleteMessage = useCallback((messageIds) => {
    if (messageBoxRef.current) {
      messageBoxRef.current.removeDeletedMessages(messageIds);
    }
  }, []);

  useEffect(() => {
    const fetchInitialUnread = async () => {
      try {
        const res = await getUnreadCount();
        if (!res.error) {
          const filtered = filterUnreadForOpenChat(res, selectedChatRef.current);
          setUnreadData({
            groupUnread: filtered.groupUnread,
            personalUnread: filtered.personalUnread,
            totalUnread: filtered.totalUnread,
            unreadBySender: filtered.unreadBySender
          });
        }
      } catch (error) {
        console.error("Failed to fetch unread counts:", error);
      }
    };
    
    fetchInitialUnread();
  }, []); 

  useUnreadSocket({
    onUpdate: handleUpdate,
    onNewMessage: handleNewMessage,
    onMarkRead: handleMarkRead,
    onEditMessage: handleEditMessage,
    onDeleteMessage: handleDeleteMessage
  });

  useEffect(() => {
    const role = sessionStorage.getItem("adminRole");
    if (role) {
      const normalizedRole = role.toLowerCase().replace(/\s/g, "");
      setUserRole(normalizedRole);
    }
    setSelectedChat(null);
  }, []);

  useEffect(() => {
    if (previousChatIdRef.current === selectedChat?.id) {
      return;
    }
    
    previousChatIdRef.current = selectedChat?.id;
    
    const refreshUnread = async () => {
      try {
        const res = await getUnreadCount();
        if (!res.error) {
          const filtered = filterUnreadForOpenChat(res, selectedChatRef.current);
          setUnreadData({
            groupUnread: filtered.groupUnread,
            personalUnread: filtered.personalUnread,
            totalUnread: filtered.totalUnread,
            unreadBySender: filtered.unreadBySender
          });
        }
      } catch (error) {
        console.error("Failed to refresh unread:", error);
      }
    };
    
    refreshUnread();
  }, [selectedChat?.id, filterUnreadForOpenChat]); 

  return (
    <div className="dashboard-wrapper">
      <Sidebar unreadMessageCount={unreadData.totalUnread} />

      <div className="c-chat-container">
        <div className="c-chat-wrapper">
          <ChatSidebar
            onSelectChat={setSelectedChat}
            selectedChatId={selectedChat?.id}
            userRole={userRole}
            unreadData={unreadData}
          />

          <MessageBox
            ref={messageBoxRef}
            selectedChat={selectedChat}
            userRole={userRole}
            onMessageSent={() => {}}
            onMessagesRead={() => {}}
          />
        </div>
      </div>
    </div>
  );
};

export default Chat;